import { STATIC_RESOURCES } from "constants/contacts";
import { lazy, memo, Suspense, useEffect } from "react";
import { Button, Container, Flex, Link, LoadingIcon, Stack, Text } from "basis";
import { usePageTracking } from "react-event-tracker";
import { useProducts } from "library/hooks/useProducts";
import { Layout } from "../../Layout/Layout";
import { CreditCard } from "../../CreditCard/CreditCard";
import { CurrencyFormat } from "../../CurrencyFormat";
import { SvgInline } from "../../SvgInline/SvgInline";
import styles from "./pay-in.module.scss";

const { tncPdfAu } = STATIC_RESOURCES;
const { tncPdfNz } = STATIC_RESOURCES;

const PDFLink = lazy(() =>
  import(/* webpackChunkName: 'pdflink' */ "./PDFLink.js"),
);

const RowItem = ({ label, rightStyle, children }) => (
  <div className={styles.rowItem}>
    <div className={styles.left}>{label}</div>
    <div className={`${styles.right} ${rightStyle}`}>{children}</div>
  </div>
);

export const PayInReview = ({
  product,
  commonData,
  paymentDetails,
  paymentMethod,
  amount,
  onConfirm,
  onEdit,
}) => {
  usePageTracking({
    page: {
      pageType: "confirmation-page",
      siteSection: "accounts",
      siteSubSection: "payment",
      pageTitle: "bank-transfer-confirmation",
    },
    user: {
      authenticationStatus: "logged in",
    },
    productId: [product.product_id],
  });
  const productsInfo = useProducts();
  const products = productsInfo.data;

  if (!products) {
    return (
      <Flex height="200" placeItems="center">
        <LoadingIcon size="medium" />
      </Flex>
    );
  }

  return (
    <Layout>
      <Layout.Header>Review payment</Layout.Header>
      <Layout.Main>
        <div className={styles.card}>
          <Container bg="grey.t05" padding="4">
            <CreditCard
              cardName={product.name}
              cardEndsWith={commonData.lastFour}
            />
          </Container>
          <Container padding="4">
            <Stack gap="6">
              <div className={styles.editWrapper}>
                <div className={styles.paymentInfo}>
                  <RowItem label="Amount">
                    <CurrencyFormat value={amount} />
                  </RowItem>
                  <RowItem label="Payment method">
                    {paymentMethod === "direct_transfer" && (
                      <Text>
                        <strong>Bank transfer</strong> (5 day lock out will
                        apply)
                      </Text>
                    )}
                  </RowItem>
                  {paymentMethod === "direct_transfer" && (
                    <RowItem label="BSB">{paymentDetails.bsb}</RowItem>
                  )}
                  {paymentMethod === "direct_transfer" && (
                    <RowItem label="Account number">
                      {paymentDetails.accountNum}
                    </RowItem>
                  )}
                </div>
                <button
                  type="button"
                  className={styles.download}
                  onClick={onEdit}
                >
                  <SvgInline className={styles.editIcon} name="icon-edit" />
                  <div className={styles.editText}>Edit payment</div>
                </button>
              </div>
              <Text textStyle="body2">
                I confirm that I am the account holder or joint account holder
                and authorise {product.name} to draw this amount from the
                account at my request and is subject to the{" "}
                {product.region === "NZ" ? (
                  <Link href={tncPdfNz} newTab>
                    New Zealand Latitude Service Centre Terms and Conditions of
                    Use
                  </Link>
                ) : (
                  <Link href={tncPdfAu} newTab>
                    Australian Latitude Service Centre Terms and Conditions of
                    Use
                  </Link>
                )}
                .
              </Text>
              <Button type="submit" onClick={onConfirm}>
                Confirm and pay
              </Button>
              {paymentMethod === "direct_transfer" && (
                <Text textStyle="legal">
                  By using this payment method you agree to funds being applied
                  to your account, and the credited amount will not be available
                  for 3 days. This means you will not have access to the
                  credited amount to make purchases or pay bills for this
                  period. If your payment is dishonoured, you may need to pay
                  the dishonoured amount immediately and other amounts owing in
                  accordance with the terms of your contract.
                </Text>
              )}
            </Stack>
          </Container>
        </div>
      </Layout.Main>
    </Layout>
  );
};

const PayInConfirmation = ({
  product,
  commonData,
  paymentDetails,
  paymentMethod,
  receiptNum,
  amount,
  onBack,
}) => {
  usePageTracking({
    page: {
      pageType: "information-page",
      siteSection: "accounts",
      siteSubSection: "payment",
      pageTitle: "bank-transfer-success",
    },
    user: {
      authenticationStatus: "logged in",
    },
    productId: [product.product_id],
  });
  const formatCurrency = (value) => {
    if (isNaN(value)) {
      return null;
    }
    return `$${amount
      .toFixed(2)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
  };

  useEffect(() => {
    document.documentElement.scrollTop = 0;
  }, []);

  const todayDate = new Date().toLocaleDateString(undefined, {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  return (
    <Layout>
      <Layout.Header>Payment request received</Layout.Header>
      <Layout.Main>
        <div className={styles.card}>
          <Container bg="grey.t05" padding="4">
            <CreditCard
              cardName={product.name}
              cardEndsWith={commonData.lastFour}
            />
          </Container>
          <div className={styles.cardBody}>
            <Suspense
              fallback={
                <Flex height="200" placeItems="center">
                  <LoadingIcon size="medium" />
                </Flex>
              }
            >
              <RowItem label="Amount">
                <CurrencyFormat value={amount} />
              </RowItem>
              <RowItem label="Payment method">
                {paymentMethod === "direct_transfer" && (
                  <Text>
                    <strong>Bank transfer</strong>
                  </Text>
                )}
              </RowItem>
              {paymentMethod === "direct_transfer" && (
                <RowItem label="BSB">
                  <Text>
                    <strong>{paymentDetails.bsb}</strong>
                  </Text>
                </RowItem>
              )}
              {paymentMethod === "direct_transfer" && (
                <RowItem label="Account number">
                  <Text>
                    <strong>{paymentDetails.accountNum}</strong>
                  </Text>
                </RowItem>
              )}
              <RowItem label="Request date">{todayDate}</RowItem>
              <RowItem label="Download receipt">
                <Flex>
                  <Text margin="0 2 0 0">
                    <strong>{receiptNum}</strong>
                  </Text>
                  <PDFLink
                    amount={formatCurrency(amount)}
                    paymentMethod={paymentMethod}
                    accountNum={paymentDetails.accountNum}
                    bsb={paymentDetails.bsb}
                    date={todayDate}
                    receiptNum={receiptNum}
                    productName={product.name}
                    lastFour={commonData.lastFour}
                    image={require("../../../images/credit-card.png").default}
                  />
                </Flex>
              </RowItem>
              <Text textStyle="body2">
                Please keep a copy of your receipt for your records.
              </Text>
              <hr />
              {paymentMethod === "direct_transfer" && (
                <Text textStyle="body2">
                  Please note that bank transfers will take 3 - 5 days to
                  process and the bank transfer method will be locked for the
                  next 5 days.
                </Text>
              )}
            </Suspense>
          </div>
        </div>
        <Button onClick={onBack}>Go to Overview</Button>
      </Layout.Main>
    </Layout>
  );
};

export const PayInConf = memo(PayInConfirmation);
